import { useEffect, useRef } from 'react';
import { createAnimationController } from '../systems/animation-controller';
import { Achievement } from '../types';
import styles from './achievement-celebration.module.css';
import gsap from 'gsap';

interface AchievementCelebrationProps {
  achievement?: Achievement;
  targetX: number;
  targetY: number;
  onComplete: () => void;
}

/**
 * AchievementCelebration
 * 
 * Renders a celebration animation when an achievement is unlocked.
 * A large star appears in the center and animates to the target position (header).
 * Blocks interactions during animation.
 */
export function AchievementCelebration({ achievement, targetX, targetY, onComplete }: AchievementCelebrationProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const starRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef(createAnimationController());

  useEffect(() => {
    const star = starRef.current;
    const container = containerRef.current;
    const overlay = overlayRef.current;
    if (!star || !container || !overlay) return;

    // Phase 1: Pop in the star and fade in text
    // (CSS handles the text fade-in via keyframes, but let's ensure star is handled by GSAP)
    gsap.set(star, { scale: 0 });
    controllerRef.current.animateToPosition(star, 0, 0, {
      duration: 0.6,
      scale: 1,
      ease: 'back.out(1.7)',
    });

    // Phase 2: Wait then move to header
    const timer = setTimeout(() => {
      const rect = star.getBoundingClientRect();
      
      // Calculate movement relative to current position (center)
      const currentCenterX = rect.left + rect.width / 2;
      const currentCenterY = rect.top + rect.height / 2;
      
      const moveX = targetX - currentCenterX;
      const moveY = targetY - currentCenterY;

      // Anticipation scale-up
      gsap.to(star, {
        scale: 1.2,
        duration: 0.2,
        ease: 'power2.out',
        onComplete: () => {
          // Start move animation
          controllerRef.current.animateToPosition(star, moveX, moveY, {
            duration: 1.0,
            scale: 0.3,
            opacity: 0.8,
            ease: 'power3.inOut',
            onComplete: () => {
              onComplete();
            }
          });
        }
      });

      // Simultaneously fade out the rest of the celebration
      gsap.to([container.querySelector(`.${styles.text}`), container.querySelector(`.${styles.achievementName}`)], {
        opacity: 0,
        y: -20,
        duration: 0.4,
        ease: 'power2.in'
      });

      gsap.to(overlay, {
        backgroundColor: 'rgba(0, 0, 0, 0)',
        duration: 0.8,
        ease: 'none',
        pointerEvents: 'none'
      });
    }, 1500); // 1.5 seconds to read the achievement

    return () => {
      clearTimeout(timer);
      controllerRef.current.killAll();
    };
  }, [targetX, targetY, onComplete]);

  return (
    <div ref={overlayRef} className={styles.overlay} role="alert" aria-live="assertive">
      <div ref={containerRef} className={styles.container}>
        <div ref={starRef} className={styles.star}>⭐</div>
        <div className={styles.text}>Achievement Unlocked!</div>
        {achievement && (
          <div className={styles.achievementName}>{achievement.name}</div>
        )}
      </div>
    </div>
  );
}
